import { useState, useCallback, startTransition } from 'react';
import { logger } from '../../utils/logger';
import { sortByKey } from '../../utils/sort';
import { useAuth } from '../../hooks/auth';
import type { Resource } from '../../types';
import { fetchResourcesFromService } from '../../services/Resources/fetchResourcesFromService';
import { mapResourceTypeToApiPath } from '../../utils/resourceTypeMapper';
import { getCachedResources, setCachedResources } from '../../utils/resourceCache';

export type { Resource };

export const useFetchResources = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [fetchedResourcesByType, setFetchedResourcesByType] = useState<Record<string, Resource[]>>(
    {}
  );
  const [fetchedTypes, setFetchedTypes] = useState<Set<string>>(new Set());
  const [failedTypes, setFailedTypes] = useState<Set<string>>(new Set());
  const [typeLoadOrder, setTypeLoadOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchProgress, setFetchProgress] = useState<{ completed: number; total: number }>({
    completed: 0,
    total: 0
  });
  const { accessToken } = useAuth();

  const clearResources = useCallback(() => {
    setResources([]);
    setFetchedResourcesByType({});
    setFetchedTypes(new Set());
    setFailedTypes(new Set());
    setTypeLoadOrder([]);
    setError(null);
    setFetchProgress({ completed: 0, total: 0 });
  }, []);

  const removeFailedTypes = useCallback((typesToRemove: string[]) => {
    setFailedTypes((prev) => {
      const updated = new Set(prev);
      typesToRemove.forEach((type) => updated.delete(type));
      return updated;
    });

    // Also update the error message to remove these types
    setError((prev) => {
      if (!prev) return null;

      // Parse existing failed types from error message
      const existingFailedArray = prev
        .split('\n')
        .filter((l) => l.includes('•'))
        .map((l) => l.replace(/.*• /, '').trim());

      // Remove the deselected types
      const remainingFailed = existingFailedArray.filter((t) => !typesToRemove.includes(t));

      if (remainingFailed.length === 0) return null;
      return `Failed Types:\n${remainingFailed.map((t) => `  • ${t}`).join('\n')}`;
    });
  }, []);

  const fetchResources = useCallback(
    async (resourceType: string, orgName: string, append: boolean = false) => {
      setLoading(true);
      setError(null);
      if (!append) {
        setResources([]); // Clear previous resources only if not appending
      }
      logger.info('Fetching resources ...', { resourceType, orgName, append });
      const started = performance.now();

      try {
        // Map the display name to the API path
        const apiResourceType = mapResourceTypeToApiPath(resourceType);

        const allResources = await fetchResourcesFromService(
          accessToken || '',
          orgName,
          apiResourceType
        );

        // Sort by name using centralized util
        const sortedResources = sortByKey(allResources, 'name');

        if (append) {
          // Append to existing resources, avoiding duplicates
          setResources((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const newResources = sortedResources.filter((r) => !existingIds.has(r.id));
            return sortByKey([...prev, ...newResources], 'name');
          });
        } else {
          setResources(sortedResources);
        }
        logger.info(
          `Successfully fetched ${sortedResources.length} resources in
        ${(performance.now() - started).toFixed(2)} ms`
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        logger.error('Error fetching resources:', { error: msg });

        setResources([]); // Set to empty array on error
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  /**
   * Fetches resources for multiple resource types in parallel with retry logic.
   * Stores them in a dictionary keyed by resource type, then displays all at once when complete.
   * Retries failed or missing types up to 3 times to ensure all data is loaded.
   */
  const fetchMultipleResourceTypes = useCallback(
    async (resourceTypes: string[], orgName: string) => {
      if (resourceTypes.length === 0) {
        clearResources();
        return;
      }

      setLoading(true);
      setFetchProgress({ completed: 0, total: resourceTypes.length });
      logger.info('Fetching resources for multiple types...', { resourceTypes, orgName });
      const started = performance.now();

      const MAX_RETRIES = 3;
      const resourceDict: Record<string, Resource[]> = {};
      const successfulTypes = new Set<string>();
      let remainingTypes = [...resourceTypes];
      let retryCount = 0;

      // Keep trying until all types are fetched or max retries reached
      while (remainingTypes.length > 0 && retryCount <= MAX_RETRIES) {
        logger.info(`API retry attempt for resource fetch`, {
          attemptNumber: retryCount + 1,
          maxRetries: MAX_RETRIES + 1,
          typeCount: remainingTypes.length,
          types: remainingTypes
        });

        // Fetch remaining types in parallel
        const fetchPromises = remainingTypes.map(async (resourceType, index) => {
          try {
            const apiResourceType = mapResourceTypeToApiPath(resourceType);

            // Check cache first
            const cached = getCachedResources(orgName, resourceType);
            if (cached && cached.length > 0) {
              logger.info(`Using cached resources for ${resourceType} (${cached.length} items)`);

              // Only update progress on FIRST attempt (not retries)
              if (retryCount === 0) {
                setFetchProgress((prev) => {
                  return {
                    completed: prev.completed + 1,
                    total: resourceTypes.length
                  };
                });
              }

              return {
                resourceType,
                resources: cached,
                success: true,
                count: cached.length,
                index,
                fromCache: true
              };
            }

            // Fetch from API if not cached
            const fetchedResources = await fetchResourcesFromService(
              accessToken || '',
              orgName,
              apiResourceType
            );

            // Store in cache for future use
            setCachedResources(orgName, resourceType, fetchedResources);

            // Only update progress on FIRST attempt (not retries)
            if (retryCount === 0) {
              setFetchProgress((prev) => {
                return {
                  completed: prev.completed + 1,
                  total: resourceTypes.length
                };
              });
            }

            return {
              resourceType,
              resources: fetchedResources,
              success: true,
              count: fetchedResources.length,
              index,
              fromCache: false
            };
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            logger.error(`Error fetching ${resourceType}:`, { error: errorMsg });
            console.error(`❌ Failed to fetch ${resourceType}:`, errorMsg);

            return {
              resourceType,
              resources: [] as Resource[],
              success: false,
              count: 0,
              index,
              fromCache: false,
              errorMessage: errorMsg
            };
          }
        });

        const results = await Promise.all(fetchPromises);

        // Process results and track what succeeded
        const failedTypes: string[] = [];

        results.forEach(
          ({ resourceType, resources: fetchedResources, success, count, errorMessage }) => {
            if (success) {
              // Store resources and mark as successful
              resourceDict[resourceType] = sortByKey(fetchedResources, 'name');
              successfulTypes.add(resourceType);
              logger.info(`Successfully fetched ${count} resources for ${resourceType}`);
            } else {
              // Track for retry
              failedTypes.push(resourceType);
              console.warn(`⚠️ ${resourceType} fetch failed: ${errorMessage || 'Unknown error'}`);
              logger.warn(`Failed to fetch ${resourceType}, will retry`, { error: errorMessage });
            }
          }
        );

        // Update progress after EACH batch (including retries)
        setFetchProgress({
          completed: successfulTypes.size,
          total: resourceTypes.length
        });

        // Check if all selected types are now in the dictionary
        const allTypesLoaded = resourceTypes.every((type) => successfulTypes.has(type));

        if (allTypesLoaded) {
          logger.info('All resource types successfully loaded');
          remainingTypes = [];
        } else {
          // Prepare for retry with failed types
          remainingTypes = failedTypes;
          retryCount++;

          if (remainingTypes.length > 0 && retryCount <= MAX_RETRIES) {
            logger.warn(
              `Retrying ${remainingTypes.length} failed types (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`
            );
            // Small delay before retry
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }
      }

      // Build final resource list from all successfully fetched types
      const allResourcesList: Resource[] = [];
      Object.values(resourceDict).forEach((resources) => {
        allResourcesList.push(...resources);
      });

      // Sort all resources by name
      const sortedAllResources = sortByKey(allResourcesList, 'name');

      const successCount = successfulTypes.size;
      const totalDuration = (performance.now() - started).toFixed(2);
      const failedTypesList = resourceTypes.filter((t) => !successfulTypes.has(t));

      if (successCount === resourceTypes.length) {
        logger.info(
          `✅ Successfully fetched ${sortedAllResources.length} resources for all ${successCount} types in ${totalDuration}ms`
        );
        logger.info(
          `Successfully fetched ${sortedAllResources.length} resources for all ${successCount} types in ${totalDuration} ms`,
          { fetchedTypes: Array.from(successfulTypes) }
        );
      } else if (successCount > 0) {
        logger.warn(
          `⚠️ Partial success: ${successCount}/${resourceTypes.length} types loaded (${sortedAllResources.length} resources)\n` +
          `   ❌ Failed: ${failedTypesList.join(', ')}\n` +
          `   ✅ Successful: ${Array.from(successfulTypes).join(', ')}`
        );
        logger.warn(
          `Partially successful: fetched ${sortedAllResources.length} resources for ${successCount}/${resourceTypes.length} types in ${totalDuration} ms`,
          {
            fetchedTypes: Array.from(successfulTypes),
            missingTypes: failedTypesList
          }
        );
      } else {
        console.error(`❌ All resource fetches failed for types: ${resourceTypes.join(', ')}`);
        logger.error('All resource type fetches failed after retries');
      }

      // Update all state in one batch using startTransition for better performance
      // This ensures the UI doesn't show partial updates
      // IMPORTANT: Merge with existing fetchedResourcesByType to preserve previously loaded types
      startTransition(() => {
        setFetchedResourcesByType((prev) => ({ ...prev, ...resourceDict }));

        // Track load order - add newly loaded types to the front
        setTypeLoadOrder((prev) => {
          const newTypes = Array.from(successfulTypes).filter((t) => !prev.includes(t));
          return [...newTypes, ...prev];
        });

        setResources((prevResources) => {
          // Merge new resources with existing ones
          const existingById = new Map(prevResources.map((r) => [r.id, r]));
          sortedAllResources.forEach((r) => existingById.set(r.id, r));
          return sortByKey(Array.from(existingById.values()), 'name');
        });

        setFetchedTypes((prev) => new Set([...prev, ...successfulTypes]));

        // Accumulate failed types (merge with previous failures)
        setFailedTypes((prev) => {
          const allFailed = new Set([...prev, ...failedTypesList]);
          // Remove any types that succeeded this time from the failed list
          successfulTypes.forEach((type) => allFailed.delete(type));
          return allFailed;
        });

        // Build cumulative error message from all failed types
        setError((prev) => {
          // Parse existing failed types from previous error message (preserving order)
          const existingFailedArray = prev
            ? prev
              .split('\n')
              .filter((l) => l.includes('•'))
              .map((l) => l.replace(/.*• /, '').trim())
            : [];

          // Remove duplicates and types that succeeded this time
          const existingFiltered = existingFailedArray.filter(
            (t) => !failedTypesList.includes(t) && !successfulTypes.has(t)
          );

          // New failures at the top, then existing ones
          const allFailed = [...failedTypesList, ...existingFiltered];

          if (allFailed.length === 0) return null;
          return `Failed Types:\n${allFailed.map((t) => `  • ${t}`).join('\n')}`;
        });

        setLoading(false);
      });
    },
    [accessToken, clearResources]
  );

  return {
    resources,
    fetchedResourcesByType,
    fetchedTypes,
    failedTypes,
    typeLoadOrder,
    loading,
    error,
    fetchProgress,
    fetchResources,
    fetchMultipleResourceTypes,
    clearResources,
    removeFailedTypes
  };
};
export default useFetchResources;
